import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Layers, Zap, ShieldCheck, Star, Loader2, ArrowRight, Link2 } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";
import { startCheckout } from "../../utils/checkout";

const money = (n) => `$${Number(n || 0) % 1 === 0 ? Number(n).toFixed(0) : Number(n).toFixed(2)}`;

export const AddonShelf = ({ addons = [], credits, verified, boostPrice, featurePrice, promo, canBuy, canCustomLink, plusPerk }) => {
  const { t } = useLang();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState("");
  const slots = addons.find((a) => a.kind === "slots");
  const priority = addons.find((a) => a.kind === "verify_priority");
  const customLink = addons.find((a) => a.kind === "custom_link");
  const unlimited = credits ? credits.monthlyAllowance == null : false;
  const showSlots = !!slots && !unlimited;

  const buy = async (id) => {
    if (!canBuy) { navigate("/login"); return; }
    setLoading(id);
    try { await startCheckout(id, { code: promo?.code }); }
    catch (e) { toast({ title: t("prc.checkoutFail"), description: e.response?.data?.detail || t("prc.tryAgain") }); setLoading(""); }
  };

  const Card = ({ icon: Icon, title, body, children, testid }) => (
    <div className="bg-card border border-border rounded-3xl p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={testid}>
      <span className="h-11 w-11 rounded-2xl bg-secondary flex items-center justify-center shrink-0"><Icon size={20} className="text-[#16a34a]" /></span>
      <h3 className="font-bold text-foreground mt-4">{title}</h3>
      <p className="text-sm text-muted-foreground mt-1.5 flex-1">{body}</p>
      <div className="mt-5">{children}</div>
    </div>
  );

  const btn = "w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2";

  return (
    <div className="space-y-5" data-testid="addon-shelf">
      <div>
        <h2 className="text-xl md:text-lg font-extrabold text-foreground flex items-center gap-2"><Layers size={18} className="text-[#16a34a]" /> {t("adn.title")}</h2>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">{t("adn.sub")}</p>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5 items-stretch">
        {showSlots && (
          <Card icon={Layers} testid="addon-slots" title={`${t("adn.slotsTitle")} — ${money(slots.amount)}`} body={t("adn.slotsBody")}>
            <>
              <button onClick={() => buy(slots.id)} disabled={!!loading} className={btn} data-testid="buy-slots-btn">
                {loading === slots.id ? <Loader2 className="animate-spin" size={17} /> : <Layers size={17} />}
                {t("adn.slotsCta", { price: money(slots.amount) })}
              </button>
            </>
          </Card>
        )}

        <Card icon={Zap} testid="addon-boost" title={`${t("adn.boostTitle")} — ${money(boostPrice)}`} body={t("adn.boostBody")}>
          <button onClick={() => navigate("/account")} className="w-full bg-secondary hover:bg-secondary/70 text-foreground font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2" data-testid="goto-boost-btn">
            <Zap size={17} className="text-[#f5b942]" /> {t("adn.boostCta")} <ArrowRight size={16} />
          </button>
        </Card>

        <Card icon={Star} testid="addon-featured" title={`${t("adn.featTitle")} — ${money(featurePrice)}`} body={t("adn.featBody")}>
          <button onClick={() => navigate("/account")} className="w-full bg-secondary hover:bg-secondary/70 text-foreground font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2" data-testid="goto-featured-btn">
            <Star size={17} className="text-[#f5b942]" /> {t("adn.featCta")} <ArrowRight size={16} />
          </button>
        </Card>

        {customLink && (
          <Card icon={Link2} testid="addon-custom-link" title={`${t("adn.linkTitle")} — ${money(customLink.amount)}`} body={t("adn.linkBody")}>
            {plusPerk ? (
              <p className="text-xs font-semibold text-[#16a34a]" data-testid="addon-link-included">{t("adn.linkIncluded")}</p>
            ) : canCustomLink ? (
              <button onClick={() => navigate("/account")} className="w-full bg-secondary hover:bg-secondary/70 text-foreground font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2" data-testid="goto-custom-link-btn">
                <Link2 size={17} className="text-[#16a34a]" /> {t("adn.linkSetIt")} <ArrowRight size={16} />
              </button>
            ) : !verified && canBuy ? (
              <p className="text-xs font-semibold text-muted-foreground" data-testid="addon-link-needs-verify">{t("adn.linkNeedsVerify")}</p>
            ) : (
              <button onClick={() => buy(customLink.id)} disabled={!!loading} className={btn} data-testid="buy-custom-link-btn">
                {loading === customLink.id ? <Loader2 className="animate-spin" size={17} /> : <Link2 size={17} />}
                {t("adn.linkCta", { price: money(customLink.amount) })}
              </button>
            )}
          </Card>
        )}

        {priority && (
          <Card icon={ShieldCheck} testid="addon-verify" title={`${t("adn.verifyTitle")} — ${money(priority.amount)}`} body={t("adn.verifyBody")}>
            {verified ? (
              <p className="text-xs font-semibold text-muted-foreground" data-testid="addon-verify-done">{t("adn.verifiedAlready")}</p>
            ) : credits?.verifyPriority ? (
              <p className="text-xs font-semibold text-[#16a34a]" data-testid="addon-verify-active">{t("adn.verifyDone")}</p>
            ) : (
              <button onClick={() => buy(priority.id)} disabled={!!loading} className={btn} data-testid="buy-priority-btn">
                {loading === priority.id ? <Loader2 className="animate-spin" size={17} /> : <ShieldCheck size={17} />}
                {t("adn.verifyCta", { price: money(priority.amount) })}
              </button>
            )}
          </Card>
        )}
      </div>

      <p className="text-xs text-muted-foreground" data-testid="boost-callout">
        {t("adn.oneOff")} · {t("prc.boostRefundPre")}{" "}
        <Link to="/terms" data-testid="boost-refund-link" className="text-[#16a34a] font-semibold hover:underline">{t("prc.refundPolicy")}</Link>.
      </p>
    </div>
  );
};
